import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AmbientBackground from '../common/AmbientBackground';
import { Button } from '../common/Button';
import VoiceInput from '../common/VoiceInput';
import VirtualSpread from '../readings/VirtualSpread';
import { useReadingsStore } from '../../store/readingsStore';
import { SpreadType } from '../../types';

const spreadContent = {
  initial: {
    title: 'Virtual Tarot Reading',
    description: 'Experience a virtual tarot reading. Perfect for learning, daily draws, or when you don\'t have your physical cards handy. Choose your reading type below to begin.'
  },
  '1-card': {
    title: 'Single Card Reading',
    description: 'A simple but powerful reading focusing on a single card to provide insight into your question.'
  },
  '3-card': {
    title: 'Three Card Reading',
    description: 'A classic spread exploring past, present, and future influences on your situation.'
  },
  '5-card': {
    title: 'Five Card Reading',
    description: 'A comprehensive reading that examines multiple aspects of your question with deeper insight.'
  },
  'celtic-cross': {
    title: 'Celtic Cross Reading',
    description: 'The most comprehensive spread, offering deep insight into complex situations through ten cards.'
  },
  'two-options': {
    title: 'Two Options Reading',
    description: 'Compare two different paths or decisions to help you choose the best option for your situation.'
  },
  'relationship': {
    title: 'Relationship Reading',
    description: 'Explore the dynamics, challenges, and potential of any relationship in your life.'
  }
};

const VirtualReading: React.FC = () => {
  const navigate = useNavigate();
  const { setCurrentReading } = useReadingsStore();
  const [step, setStep] = useState<'choose' | 'question' | 'spread'>('choose');
  const [spreadType, setSpreadType] = useState<SpreadType>('1-card');
  const [key, setKey] = useState(0);
  const [question, setQuestion] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCards, setGeneratedCards] = useState<any[] | null>(null);

  const content = step === 'spread' ? spreadContent[spreadType] : spreadContent.initial;

  const handleSpreadGenerated = (cards: any[]) => {
    setGeneratedCards(cards);
  };

  const handleGetInterpretation = async () => {
    if (isGenerating || !generatedCards) return;

    try {
      setIsGenerating(true);

      const readingData = {
        subject: 'Virtual Reading',
        question: question || 'What guidance do the cards have for me?',
        context: `A ${spreadType === '1-card' ? 'single' : spreadType === '3-card' ? 'three' : 'five'} card virtual reading exploring: ${question || 'What guidance do the cards have for me?'}`,
        spreadType,
        cards: generatedCards,
        is_virtual: true
      };

      await setCurrentReading(readingData);
      navigate('/interpretation');
    } catch (error) {
      console.error('Error setting reading:', error);
      setIsGenerating(false);
    }
  };

  const handleSpreadTypeSelect = (type: SpreadType) => {
    setSpreadType(type);
    setKey(prev => prev + 1); // Increment key to force VirtualSpread remount
    if (type === '1-card') {
      setStep('question');
    } else {
      setStep('question');
    }
  };

  const handleQuestionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (question.trim()) {
      setKey(prev => prev + 1); // Increment key to force VirtualSpread remount
      setStep('spread');
    }
  };

  // Reset state when returning to choose step
  const handleBack = () => {
    setStep('choose');
    setQuestion('');
    setGeneratedCards(null);
    setKey(prev => prev + 1); // Increment key to force VirtualSpread remount
  };

  // Reset all state when component unmounts or user navigates away
  React.useEffect(() => {
    return () => {
      setStep('choose');
      setSpreadType('1-card');
      setQuestion('');
      setGeneratedCards(null);
      setKey(0);
      setIsGenerating(false);
    };
  }, []);

  return (
    <AmbientBackground mood="sun" backgroundImage="/bg-still-glow.png">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-4xl mx-auto px-4 py-12"
      >
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-ink dark:text-parchment mb-4 text-center">
          {content.title}
        </h1>
        <p className="text-lg text-ink/70 dark:text-parchment/60 text-center mb-8">
          {content.description}
        </p>

        {step === 'choose' && (
          <div className="text-center space-y-6">
            <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6">
              <h2 className="font-display text-2xl font-semibold text-sun dark:text-ember mb-4">
                Choose Your Reading Type
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto mb-6">
                <button
                  onClick={() => handleSpreadTypeSelect('1-card')}
                  className="p-6 bg-parchment/60 dark:bg-shadow/60 rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                >
                  <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                    Single Card
                  </h3>
                  <p className="text-sm text-ink/70 dark:text-parchment/60">
                    Draw a single card for guidance and insight.
                  </p>
                </button>

                <button
                  onClick={() => handleSpreadTypeSelect('3-card')}
                  className="p-6 bg-parchment/60 dark:bg-shadow/60 rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                >
                  <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                    Three Card Spread
                  </h3>
                  <p className="text-sm text-ink/70 dark:text-parchment/60">
                    One of the most common spreads to get more in depth guidance for a question or situation you have in mind.
                  </p>
                </button>

                <button
                  onClick={() => handleSpreadTypeSelect('5-card')}
                  className="p-6 bg-parchment/60 dark:bg-shadow/60 rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                >
                  <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                    Five Card Spread
                  </h3>
                  <p className="text-sm text-ink/70 dark:text-parchment/60">
                    A more complex reading that explores a situation or question you have while considering what other influences may be at play.
                  </p>
                </button>

                <button
                  onClick={() => handleSpreadTypeSelect('celtic-cross')}
                  className="p-6 bg-parchment/60 dark:bg-shadow/60 rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                >
                  <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                    Celtic Cross
                  </h3>
                  <p className="text-sm text-ink/70 dark:text-parchment/60">
                    A comprehensive ten-card spread for deep insight into complex situations.
                  </p>
                </button>

                <button
                  onClick={() => handleSpreadTypeSelect('two-options')}
                  className="p-6 bg-parchment/60 dark:bg-shadow/60 rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                >
                  <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                    Two Options
                  </h3>
                  <p className="text-sm text-ink/70 dark:text-parchment/60">
                    Compare two different paths or decisions to help you choose the best option.
                  </p>
                </button>

                <button
                  onClick={() => handleSpreadTypeSelect('relationship')}
                  className="p-6 bg-parchment/60 dark:bg-shadow/60 rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                >
                  <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                    Relationship
                  </h3>
                  <p className="text-sm text-ink/70 dark:text-parchment/60">
                    Explore the dynamics, challenges, and potential of a relationship.
                  </p>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6">
                <h3 className="font-display text-xl font-semibold text-sun dark:text-ember mb-3">
                  Save Your Reading
                </h3>
                <p className="text-ink/70 dark:text-parchment/60">
                  After your reading, you can save it to your journal to revisit later. Add your own interpretations and notes to track your journey.
                </p>
              </div>

              <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6">
                <h3 className="font-display text-xl font-semibold text-sun dark:text-ember mb-3">
                  Enhanced Insights
                </h3>
                <p className="text-ink/70 dark:text-parchment/60">
                  Get detailed interpretations for your spread, including card meanings, symbolism, and guidance for your journey.
                </p>
              </div>
            </div>
          </div>
        )}

        {step === 'question' && (
          <form onSubmit={handleQuestionSubmit} className="max-w-2xl mx-auto">
            <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6">
              <h2 className="font-display text-2xl font-semibold text-sun dark:text-ember mb-4">
                What Would You Like to Explore?
              </h2>
              <p className="text-ink/70 dark:text-parchment/60 mb-6">
                Take a moment to formulate your question. The clearer your intention, the more meaningful your reading will be.
              </p>
              <div className="space-y-4">
                <div>
                  <label htmlFor="question" className="font-display block text-sm font-medium text-ink dark:text-parchment/60 mb-2">
                    Your Question
                  </label>
                  <div className="relative">
                    <textarea
                      id="question"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      className="w-full px-3 py-2 pr-12 border border-mist dark:border-dusk rounded-lg focus:outline-none focus:ring-2 focus:ring-sun/40 dark:focus:ring-ember/40 bg-parchment/60 dark:bg-shadow/60"
                      rows={3}
                      placeholder="What would you like guidance about?"
                    />
                    <div className="absolute right-2 top-2">
                      <VoiceInput
                        onTranscript={(text) => setQuestion(prev => prev + (prev ? ' ' : '') + text)}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!question.trim()}
                  >
                    Draw Cards
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}

        {step === 'spread' && (
          <div className="space-y-6">
            <VirtualSpread
              key={key}
              spreadType={spreadType}
              onSpreadGenerated={handleSpreadGenerated}
            />

            <div className="flex justify-center gap-4 mt-8">
              <Button
                variant="outline"
                onClick={handleBack}
              >
                Start Over
              </Button>
              <Button
                variant="primary"
                onClick={handleGetInterpretation}
                isLoading={isGenerating}
                disabled={isGenerating || !generatedCards}
              >
                {isGenerating ? 'Generating Interpretation...' : 'Get Interpretation'}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-8 text-center space-x-4">
          <Link
            to="/guide/101"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            Tarot 101
          </Link>
          <span className="text-mist">•</span>
          <Link
            to="/center"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            Tarot Center
          </Link>
          <span className="text-mist">•</span>
          <Link
            to="/faq"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            FAQ
          </Link>
        </div>
      </motion.div>
    </AmbientBackground>
  );
};

export default VirtualReading;
